import { z } from "zod";
import { OnboardingTaskTypeSchema } from "./onboarding";
import { OpportunityStatusSchema } from "./opportunities";
import { QuoteLineTypeSchema, QuoteStatusSchema } from "./quotes";
import { TaskMetadataEntityTypeSchema } from "./tasks";

// ============================================================================
// GENERIC ACTIVITY EVENT INFRASTRUCTURE
// These types/schemas are shared across all entity types
// ============================================================================

export const ACTIVITY_EVENT_ACTOR_TYPE_VALUES = [
  "USER",
  "AGENT_WORKFLOW_RUN",
  "AGENT_SDK_THREAD",
  "EXTERNAL",
  "SYSTEM",
] as const;
export const ActivityEventActorTypeSchema = z.enum(
  ACTIVITY_EVENT_ACTOR_TYPE_VALUES
);
export type ActivityEventActorType = z.infer<
  typeof ActivityEventActorTypeSchema
>;
export const ACTIVITY_EVENT_ACTOR_TYPE = ActivityEventActorTypeSchema.enum;

const BaseActivityEventSchema = z.object({
  id: z.string(),
  actorId: z.string().nullable(),
  actorType: ActivityEventActorTypeSchema,
  actorName: z.string().nullable(),
  userId: z.string().nullable(),
  agentWorkflowId: z.string().optional(),
  organizationId: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// ============================================================================
// OPPORTUNITY-SPECIFIC ACTIVITY EVENTS
// Includes both opportunity events and quote events logged to opportunities
// ============================================================================

export const OPPORTUNITY_EVENT_TYPE_VALUES = [
  "OPPORTUNITY_CREATED",
  "OPPORTUNITY_UPDATED",
  "OPPORTUNITY_ARCHIVED",
  "OPPORTUNITY_UNARCHIVED",
] as const;

export const OPPORTUNITY_AGENT_EVENT_TYPE_VALUES = [
  "AGENT_ACTION_EXECUTED",
  "AGENT_TRIGGER_EXECUTED",
  "AGENT_USER_MESSAGE",
] as const;

export const OPPORTUNITY_QUOTE_EVENT_TYPE_VALUES = [
  "QUOTE_ADDED_TO_OPPORTUNITY",
  "QUOTE_REMOVED_FROM_OPPORTUNITY",
  "QUOTE_UPDATED",
  "QUOTE_FINALIZED",
  "QUOTE_VALIDATED",
  "QUOTE_REVISION_CREATED",
  "QUOTE_SIGNED",
  "QUOTE_PDF_GENERATED",
  "QUOTE_VIEWED",
  "QUOTE_DELETED",
  "QUOTE_ARCHIVED",
  "QUOTE_UNARCHIVED",
  "QUOTE_PUBLIC_ACCESS_REVOKED",
  "QUOTE_PUBLIC_ACCESS_RESTORED",
] as const;

export const OPPORTUNITY_TASK_EVENT_TYPE_VALUES = [
  "TASK_PENDING_APPROVAL",
  "TASK_APPROVED",
  "TASK_CANCELLED",
] as const;

const ALL_OPPORTUNITY_EVENT_TYPE_VALUES = [
  ...OPPORTUNITY_EVENT_TYPE_VALUES,
  ...OPPORTUNITY_AGENT_EVENT_TYPE_VALUES,
  ...OPPORTUNITY_QUOTE_EVENT_TYPE_VALUES,
  ...OPPORTUNITY_TASK_EVENT_TYPE_VALUES,
] as const;

export const OpportunityEventTypeSchema = z.enum(ALL_OPPORTUNITY_EVENT_TYPE_VALUES);
export const OPPORTUNITY_EVENT_TYPE = OpportunityEventTypeSchema.enum;

export const OpportunityCreatedMetadataSchema = z.object({
  name: z.string(),
  status: OpportunityStatusSchema,
  amount: z.string(),
  probability: z.number().int().optional(),
  salesRepUserId: z.string().nullable().optional(),
  stageId: z.string().nullable().optional(),
});
export type OpportunityCreatedMetadata = z.infer<
  typeof OpportunityCreatedMetadataSchema
>;

export const OpportunityUpdatedMetadataSchema = z.object({
  oldStatus: OpportunityStatusSchema.optional(),
  newStatus: OpportunityStatusSchema.optional(),
  oldStageId: z.string().nullable().optional(),
  newStageId: z.string().nullable().optional(),
  oldName: z.string().optional(),
  newName: z.string().optional(),
  oldAmount: z.string().optional(),
  newAmount: z.string().optional(),
  oldProbability: z.number().int().optional(),
  newProbability: z.number().int().optional(),
  oldCompanyId: z.string().nullable().optional(),
  newCompanyId: z.string().nullable().optional(),
  oldSalesRepUserId: z.string().nullable().optional(),
  newSalesRepUserId: z.string().nullable().optional(),
  oldEstimatorUserIds: z.array(z.string()).optional(),
  newEstimatorUserIds: z.array(z.string()).optional(),
  oldPersonIds: z.array(z.string()).optional(),
  newPersonIds: z.array(z.string()).optional(),
  oldLocationId: z.string().nullable().optional(),
  newLocationId: z.string().nullable().optional(),
});
export type OpportunityUpdatedMetadata = z.infer<
  typeof OpportunityUpdatedMetadataSchema
>;

export const OpportunityArchivedMetadataSchema = z.object({
  name: z.string(),
});
export type OpportunityArchivedMetadata = z.infer<
  typeof OpportunityArchivedMetadataSchema
>;

export const OpportunityUnarchivedMetadataSchema = z.object({
  name: z.string(),
});
export type OpportunityUnarchivedMetadata = z.infer<
  typeof OpportunityUnarchivedMetadataSchema
>;

export const EmailContentSchema = z.object({
  from: z.string().optional(),
  to: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  bcc: z.array(z.string()).optional(),
  subject: z.string().optional(),
  body: z.string().optional(),
  assetIds: z.array(z.string()).optional(),
});
export type EmailContent = z.infer<typeof EmailContentSchema>;

export const SmsContentSchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  body: z.string().optional(),
});
export type SmsContent = z.infer<typeof SmsContentSchema>;

export const AgentActionExecutedMetadataSchema = z
  .object({
    actionId: z.string(),
    actionName: z.string(),
    operationId: z.string().optional(),
    messageId: z.string().optional(),
    output: z.unknown(),
    emailContent: EmailContentSchema.optional(),
    smsContent: SmsContentSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (!value.operationId && !value.messageId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Either operationId or messageId is required",
        path: ["operationId"],
      });
    }
  });
export type AgentActionExecutedMetadata = z.infer<
  typeof AgentActionExecutedMetadataSchema
>;

export const AgentTriggerExecutedMetadataSchema = z.object({
  triggerId: z.string(),
  triggerName: z.string(),
  messageType: z.string().optional(),
  messageLabel: z.string().nullable().optional(),
  operationId: z.string(),
  input: z.unknown(),
  output: z.unknown(),
  emailContent: EmailContentSchema.optional(),
  smsContent: SmsContentSchema.optional(),
});
export type AgentTriggerExecutedMetadata = z.infer<
  typeof AgentTriggerExecutedMetadataSchema
>;

export const AgentUserMessageMetadataSchema = z.object({
  messageType: z.string(),
  messageLabel: z.string().nullable().optional(),
  messageId: z.string(),
  message: z.string(),
  emailContent: EmailContentSchema.optional(),
  smsContent: SmsContentSchema.optional(),
});
export type AgentUserMessageMetadata = z.infer<
  typeof AgentUserMessageMetadataSchema
>;

export const QuoteAddedToOpportunityMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuoteAddedToOpportunityMetadata = z.infer<
  typeof QuoteAddedToOpportunityMetadataSchema
>;

export const QuoteRemovedFromOpportunityMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuoteRemovedFromOpportunityMetadata = z.infer<
  typeof QuoteRemovedFromOpportunityMetadataSchema
>;

/** Schema for an individual line item change within a quote update */
export const LineItemChangeSchema = z.object({
  lineId: z.string(),
  changeType: z.enum(["ADDED", "REMOVED", "MODIFIED"]),
  lineType: QuoteLineTypeSchema,
  description: z.string().optional(),
  oldDescription: z.string().optional(),
  newDescription: z.string().optional(),
  oldItemId: z.string().nullable().optional(),
  newItemId: z.string().nullable().optional(),
  oldItemName: z.string().nullable().optional(),
  newItemName: z.string().nullable().optional(),
  itemName: z.string().nullable().optional(),
});
export type LineItemChange = z.infer<typeof LineItemChangeSchema>;

export const QuoteUpdatedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
  oldStatus: QuoteStatusSchema.optional(),
  newStatus: QuoteStatusSchema.optional(),
  oldName: z.string().nullable().optional(),
  newName: z.string().nullable().optional(),
  oldCustomerCompanyId: z.string().nullable().optional(),
  newCustomerCompanyId: z.string().nullable().optional(),
  oldSalesRepUserId: z.string().nullable().optional(),
  newSalesRepUserId: z.string().nullable().optional(),
  oldLocationId: z.string().nullable().optional(),
  newLocationId: z.string().nullable().optional(),
  oldPersonIds: z.array(z.string()).optional(),
  newPersonIds: z.array(z.string()).optional(),
  oldExpirationDate: z.string().nullable().optional(),
  newExpirationDate: z.string().nullable().optional(),
  oldQuoteDate: z.string().nullable().optional(),
  newQuoteDate: z.string().nullable().optional(),
  oldPaymentTerms: z.string().nullable().optional(),
  newPaymentTerms: z.string().nullable().optional(),
  oldDeliveryTerms: z.string().nullable().optional(),
  newDeliveryTerms: z.string().nullable().optional(),
  termsOfServiceChanged: z.boolean().optional(),
  oldShowTotals: z.boolean().optional(),
  newShowTotals: z.boolean().optional(),
  oldTotal: z.string().nullable().optional(),
  newTotal: z.string().nullable().optional(),
  oldSubtotal: z.string().nullable().optional(),
  newSubtotal: z.string().nullable().optional(),
  lineItemsChanged: z.boolean().optional(),
  itemsAdded: z.number().optional(),
  itemsRemoved: z.number().optional(),
  discountsChanged: z.boolean().optional(),
  lineItemChanges: z.array(LineItemChangeSchema).optional(),
});
export type QuoteUpdatedMetadata = z.infer<typeof QuoteUpdatedMetadataSchema>;

export const QuoteFinalizedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuoteFinalizedMetadata = z.infer<
  typeof QuoteFinalizedMetadataSchema
>;

export const QuoteValidatedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
  isValid: z.boolean(),
  validationErrors: z.array(z.string()).optional(),
});
export type QuoteValidatedMetadata = z.infer<
  typeof QuoteValidatedMetadataSchema
>;

export const QuoteRevisionCreatedMetadataSchema = z.object({
  originalQuoteId: z.string(),
  newQuoteId: z.string(),
  newRevisionNumber: z.number(),
});
export type QuoteRevisionCreatedMetadata = z.infer<
  typeof QuoteRevisionCreatedMetadataSchema
>;

export const QuoteSignedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
  signerName: z.string(),
  signerEmail: z.string(),
  signatureDate: z.string(),
  ipAddress: z.string(),
});
export type QuoteSignedMetadata = z.infer<typeof QuoteSignedMetadataSchema>;

export const QuotePdfGeneratedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
  assetId: z.string(),
});
export type QuotePdfGeneratedMetadata = z.infer<
  typeof QuotePdfGeneratedMetadataSchema
>;

export const QuoteViewedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
  ipAddress: z.string().nullable(),
  viewerLocation: z.string().nullable().optional(),
});
export type QuoteViewedMetadata = z.infer<typeof QuoteViewedMetadataSchema>;

export const QuoteDeletedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuoteDeletedMetadata = z.infer<typeof QuoteDeletedMetadataSchema>;

export const QuoteArchivedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuoteArchivedMetadata = z.infer<typeof QuoteArchivedMetadataSchema>;

export const QuoteUnarchivedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuoteUnarchivedMetadata = z.infer<
  typeof QuoteUnarchivedMetadataSchema
>;

export const QuotePublicAccessRevokedMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuotePublicAccessRevokedMetadata = z.infer<
  typeof QuotePublicAccessRevokedMetadataSchema
>;

export const QuotePublicAccessRestoredMetadataSchema = z.object({
  quoteId: z.string(),
  quoteNumber: z.number(),
});
export type QuotePublicAccessRestoredMetadata = z.infer<
  typeof QuotePublicAccessRestoredMetadataSchema
>;

export const TaskPendingApprovalMetadataSchema = z.object({
  taskId: z.string(),
  actionName: z.string(),
  entityId: z.string().optional(),
  entityType: TaskMetadataEntityTypeSchema.optional(),
  approverUserIds: z.array(z.string()),
  approverNames: z.array(z.string()),
});
export type TaskPendingApprovalMetadata = z.infer<
  typeof TaskPendingApprovalMetadataSchema
>;

export const TaskApprovedMetadataSchema = z.object({
  taskId: z.string(),
  actionName: z.string(),
  entityId: z.string().optional(),
  entityType: TaskMetadataEntityTypeSchema.optional(),
  approverUserId: z.string(),
  approverName: z.string(),
});
export type TaskApprovedMetadata = z.infer<typeof TaskApprovedMetadataSchema>;

export const TaskCancelledMetadataSchema = z.object({
  taskId: z.string(),
  actionName: z.string(),
  entityId: z.string().optional(),
  entityType: TaskMetadataEntityTypeSchema.optional(),
  cancellerUserId: z.string(),
  cancellerName: z.string(),
});
export type TaskCancelledMetadata = z.infer<typeof TaskCancelledMetadataSchema>;

const BaseOpportunityEventSchema = BaseActivityEventSchema.extend({
  entityType: z.literal("OPPORTUNITY"),
  entityId: z.string(),
});

const OpportunityCreatedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("OPPORTUNITY_CREATED"),
  eventMetadata: OpportunityCreatedMetadataSchema,
});

const OpportunityUpdatedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("OPPORTUNITY_UPDATED"),
  eventMetadata: OpportunityUpdatedMetadataSchema,
});

const OpportunityArchivedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("OPPORTUNITY_ARCHIVED"),
  eventMetadata: OpportunityArchivedMetadataSchema,
});

const OpportunityUnarchivedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("OPPORTUNITY_UNARCHIVED"),
  eventMetadata: OpportunityUnarchivedMetadataSchema,
});

const AgentActionExecutedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("AGENT_ACTION_EXECUTED"),
  eventMetadata: AgentActionExecutedMetadataSchema,
});

const AgentTriggerExecutedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("AGENT_TRIGGER_EXECUTED"),
  eventMetadata: AgentTriggerExecutedMetadataSchema,
});

const AgentUserMessageEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("AGENT_USER_MESSAGE"),
  eventMetadata: AgentUserMessageMetadataSchema,
});

const QuoteAddedToOpportunityEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_ADDED_TO_OPPORTUNITY"),
  eventMetadata: QuoteAddedToOpportunityMetadataSchema,
});

const QuoteRemovedFromOpportunityEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_REMOVED_FROM_OPPORTUNITY"),
  eventMetadata: QuoteRemovedFromOpportunityMetadataSchema,
});

const QuoteUpdatedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_UPDATED"),
  eventMetadata: QuoteUpdatedMetadataSchema,
});

const QuoteFinalizedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_FINALIZED"),
  eventMetadata: QuoteFinalizedMetadataSchema,
});

const QuoteValidatedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_VALIDATED"),
  eventMetadata: QuoteValidatedMetadataSchema,
});

const QuoteRevisionCreatedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_REVISION_CREATED"),
  eventMetadata: QuoteRevisionCreatedMetadataSchema,
});

const QuoteSignedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_SIGNED"),
  eventMetadata: QuoteSignedMetadataSchema,
});

const QuotePdfGeneratedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_PDF_GENERATED"),
  eventMetadata: QuotePdfGeneratedMetadataSchema,
});

const QuoteViewedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_VIEWED"),
  eventMetadata: QuoteViewedMetadataSchema,
});

const QuoteDeletedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_DELETED"),
  eventMetadata: QuoteDeletedMetadataSchema,
});

const QuoteArchivedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_ARCHIVED"),
  eventMetadata: QuoteArchivedMetadataSchema,
});

const QuoteUnarchivedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_UNARCHIVED"),
  eventMetadata: QuoteUnarchivedMetadataSchema,
});

const QuotePublicAccessRevokedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_PUBLIC_ACCESS_REVOKED"),
  eventMetadata: QuotePublicAccessRevokedMetadataSchema,
});

const QuotePublicAccessRestoredEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("QUOTE_PUBLIC_ACCESS_RESTORED"),
  eventMetadata: QuotePublicAccessRestoredMetadataSchema,
});

const TaskPendingApprovalEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("TASK_PENDING_APPROVAL"),
  eventMetadata: TaskPendingApprovalMetadataSchema,
});

const TaskApprovedEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("TASK_APPROVED"),
  eventMetadata: TaskApprovedMetadataSchema,
});

const TaskCancelledEventSchema = BaseOpportunityEventSchema.extend({
  eventType: z.literal("TASK_CANCELLED"),
  eventMetadata: TaskCancelledMetadataSchema,
});

export const OpportunityActivityEventSchema = z.discriminatedUnion("eventType", [
  OpportunityCreatedEventSchema,
  OpportunityUpdatedEventSchema,
  OpportunityArchivedEventSchema,
  OpportunityUnarchivedEventSchema,
  AgentActionExecutedEventSchema,
  AgentTriggerExecutedEventSchema,
  AgentUserMessageEventSchema,
  QuoteAddedToOpportunityEventSchema,
  QuoteRemovedFromOpportunityEventSchema,
  QuoteUpdatedEventSchema,
  QuoteFinalizedEventSchema,
  QuoteValidatedEventSchema,
  QuoteRevisionCreatedEventSchema,
  QuoteSignedEventSchema,
  QuotePdfGeneratedEventSchema,
  QuoteViewedEventSchema,
  QuoteDeletedEventSchema,
  QuoteArchivedEventSchema,
  QuoteUnarchivedEventSchema,
  QuotePublicAccessRevokedEventSchema,
  QuotePublicAccessRestoredEventSchema,
  TaskPendingApprovalEventSchema,
  TaskApprovedEventSchema,
  TaskCancelledEventSchema,
]);
export type OpportunityActivityEvent = z.infer<
  typeof OpportunityActivityEventSchema
>;

// ============================================================================
// ONBOARDING-TASK-SPECIFIC ACTIVITY EVENTS
// The check/uncheck audit trail for onboarding board tasks
// ============================================================================

/** Event types recorded when an onboarding task is checked or unchecked. */
export const ONBOARDING_TASK_EVENT_TYPE_VALUES = [
  "ONBOARDING_TASK_COMPLETED",
  "ONBOARDING_TASK_REOPENED",
] as const;

/** Zod schema validating an onboarding task event type. */
export const OnboardingTaskEventTypeSchema = z.enum(
  ONBOARDING_TASK_EVENT_TYPE_VALUES,
);

/** Enum-style accessor for onboarding task event type values. */
export const ONBOARDING_TASK_EVENT_TYPE = OnboardingTaskEventTypeSchema.enum;

/** Metadata recorded when an onboarding task is checked off. */
export const OnboardingTaskCompletedMetadataSchema = z.object({
  taskName: z.string(),
  taskType: OnboardingTaskTypeSchema,
});

/** Metadata recorded when an onboarding task is checked off. */
export type OnboardingTaskCompletedMetadata = z.infer<
  typeof OnboardingTaskCompletedMetadataSchema
>;

/** Metadata recorded when a completed onboarding task is reopened. */
export const OnboardingTaskReopenedMetadataSchema = z.object({
  taskName: z.string(),
  taskType: OnboardingTaskTypeSchema,
});

/** Metadata recorded when a completed onboarding task is reopened. */
export type OnboardingTaskReopenedMetadata = z.infer<
  typeof OnboardingTaskReopenedMetadataSchema
>;

const BaseOnboardingTaskEventSchema = BaseActivityEventSchema.extend({
  entityType: z.literal("ONBOARDING_TASK"),
  entityId: z.string(),
});

const OnboardingTaskCompletedEventSchema = BaseOnboardingTaskEventSchema.extend(
  {
    eventType: z.literal("ONBOARDING_TASK_COMPLETED"),
    eventMetadata: OnboardingTaskCompletedMetadataSchema,
  },
);

const OnboardingTaskReopenedEventSchema = BaseOnboardingTaskEventSchema.extend({
  eventType: z.literal("ONBOARDING_TASK_REOPENED"),
  eventMetadata: OnboardingTaskReopenedMetadataSchema,
});

/** The union of activity events recorded against onboarding tasks. */
export const OnboardingTaskActivityEventSchema = z.discriminatedUnion(
  "eventType",
  [OnboardingTaskCompletedEventSchema, OnboardingTaskReopenedEventSchema],
);

/** An activity event recorded against an onboarding task. */
export type OnboardingTaskActivityEvent = z.infer<
  typeof OnboardingTaskActivityEventSchema
>;

// ============================================================================
// ITEM-SPECIFIC ACTIVITY EVENTS
// The per-field change log for item writes
// ============================================================================

/** Event types recorded when an item is written. */
export const ITEM_EVENT_TYPE_VALUES = ["ITEM_CREATED", "ITEM_UPDATED"] as const;

/** Zod schema validating an item event type. */
export const ItemEventTypeSchema = z.enum(ITEM_EVENT_TYPE_VALUES);

/** Enum-style accessor for item event type values. */
export const ITEM_EVENT_TYPE = ItemEventTypeSchema.enum;

/** The part of an item a changed field belongs to. */
export const ITEM_CHANGE_GROUP_VALUES = [
  "details",
  "cost",
  "bom",
  "pricing",
  "priceModel",
  "files",
  "lifecycle",
] as const;

/** Zod schema validating an item change group. */
export const ItemChangeGroupSchema = z.enum(ITEM_CHANGE_GROUP_VALUES);

/** The part of an item a changed field belongs to. */
export type ItemChangeGroup = z.infer<typeof ItemChangeGroupSchema>;

/**
 * One field's old and new value within an item write. `field` is a dot path:
 * a column name for a scalar (`weight`), or a row key for a keyed row
 * (`costBreaks.100`, `operations.<rowId>.runTimePerItem`).
 */
export const ItemFieldChangeSchema = z.object({
  field: z.string(),
  group: ItemChangeGroupSchema,
  from: z.unknown(),
  to: z.unknown(),
});

/** One field's old and new value within an item write. */
export type ItemFieldChange = z.infer<typeof ItemFieldChangeSchema>;

/**
 * Metadata recorded for an item create or update. A created item lists its
 * starting values as changes with a null `from`, so one reader serves both
 * event types.
 */
export const ItemChangeMetadataSchema = z.object({
  itemName: z.string(),
  revision: z.string(),
  changes: z.array(ItemFieldChangeSchema).min(1),
  reason: z.string().optional(),
  syncId: z.string().optional(),
  importId: z.string().optional(),
  fileName: z.string().optional(),
  quoteNumber: z.number().optional(),
});

/** Metadata recorded for an item create or update. */
export type ItemChangeMetadata = z.infer<typeof ItemChangeMetadataSchema>;

const BaseItemEventSchema = BaseActivityEventSchema.extend({
  entityType: z.literal("ITEM"),
  entityId: z.string(),
});

const ItemCreatedEventSchema = BaseItemEventSchema.extend({
  eventType: z.literal("ITEM_CREATED"),
  eventMetadata: ItemChangeMetadataSchema,
});

const ItemUpdatedEventSchema = BaseItemEventSchema.extend({
  eventType: z.literal("ITEM_UPDATED"),
  eventMetadata: ItemChangeMetadataSchema,
});

/** The union of activity events recorded against items. */
export const ItemActivityEventSchema = z.discriminatedUnion("eventType", [
  ItemCreatedEventSchema,
  ItemUpdatedEventSchema,
]);

/** An activity event recorded against an item. */
export type ItemActivityEvent = z.infer<typeof ItemActivityEventSchema>;

// ============================================================================
// COMBINED ACTIVITY EVENT TYPES
// These union types combine all entity-specific events into one system
// ============================================================================

export const ActivityEventSchema = z.discriminatedUnion("entityType", [
  OpportunityActivityEventSchema,
  OnboardingTaskActivityEventSchema,
  ItemActivityEventSchema,
]);
export type ActivityEvent = z.infer<typeof ActivityEventSchema>;

export const ACTIVITY_EVENT_ENTITY_TYPE_VALUES = [
  "OPPORTUNITY",
  "ONBOARDING_TASK",
  "ITEM",
] as const;
export type ActivityEventEntityType =
  (typeof ACTIVITY_EVENT_ENTITY_TYPE_VALUES)[number];
export const ActivityEventEntityTypeSchema = z.enum(ACTIVITY_EVENT_ENTITY_TYPE_VALUES);
export const ACTIVITY_EVENT_ENTITY_TYPE = ActivityEventEntityTypeSchema.enum;

export const ACTIVITY_EVENT_TYPE_VALUES = [
  ...ALL_OPPORTUNITY_EVENT_TYPE_VALUES,
  ...ONBOARDING_TASK_EVENT_TYPE_VALUES,
  ...ITEM_EVENT_TYPE_VALUES,
] as const;
export type ActivityEventType = (typeof ACTIVITY_EVENT_TYPE_VALUES)[number];

// ============================================================================
// EVENT TYPE CATEGORIES & LABELS
// Canonical mapping from event types to display categories and human-readable labels
// ============================================================================

/** Category grouping for activity event types. */
export type ActivityEventCategory =
  | "opportunity"
  | "quote"
  | "agent"
  | "system"
  | "onboarding"
  | "item";

/** Maps each event type to its display category. */
export const ACTIVITY_EVENT_TYPE_CATEGORY: Record<ActivityEventType, ActivityEventCategory> = {
  OPPORTUNITY_CREATED: "opportunity",
  OPPORTUNITY_UPDATED: "opportunity",
  OPPORTUNITY_ARCHIVED: "opportunity",
  OPPORTUNITY_UNARCHIVED: "opportunity",
  AGENT_ACTION_EXECUTED: "agent",
  AGENT_TRIGGER_EXECUTED: "agent",
  AGENT_USER_MESSAGE: "agent",
  QUOTE_ADDED_TO_OPPORTUNITY: "quote",
  QUOTE_REMOVED_FROM_OPPORTUNITY: "quote",
  QUOTE_UPDATED: "quote",
  QUOTE_FINALIZED: "quote",
  QUOTE_VALIDATED: "quote",
  QUOTE_REVISION_CREATED: "quote",
  QUOTE_SIGNED: "quote",
  QUOTE_PDF_GENERATED: "quote",
  QUOTE_VIEWED: "quote",
  QUOTE_DELETED: "quote",
  QUOTE_ARCHIVED: "quote",
  QUOTE_UNARCHIVED: "quote",
  QUOTE_PUBLIC_ACCESS_REVOKED: "quote",
  QUOTE_PUBLIC_ACCESS_RESTORED: "quote",
  TASK_PENDING_APPROVAL: "system",
  TASK_APPROVED: "system",
  TASK_CANCELLED: "system",
  ONBOARDING_TASK_COMPLETED: "onboarding",
  ONBOARDING_TASK_REOPENED: "onboarding",
  ITEM_CREATED: "item",
  ITEM_UPDATED: "item",
};

/** Human-readable short labels for each event type. */
export const ACTIVITY_EVENT_TYPE_LABELS: Record<ActivityEventType, string> = {
  OPPORTUNITY_CREATED: "Created opportunity",
  OPPORTUNITY_UPDATED: "Updated opportunity",
  OPPORTUNITY_ARCHIVED: "Archived opportunity",
  OPPORTUNITY_UNARCHIVED: "Unarchived opportunity",
  AGENT_ACTION_EXECUTED: "Executed action",
  AGENT_TRIGGER_EXECUTED: "Trigger executed",
  AGENT_USER_MESSAGE: "Sent message",
  QUOTE_ADDED_TO_OPPORTUNITY: "Added quote to opportunity",
  QUOTE_REMOVED_FROM_OPPORTUNITY: "Removed quote from opportunity",
  QUOTE_UPDATED: "Updated quote",
  QUOTE_FINALIZED: "Finalized quote",
  QUOTE_VALIDATED: "Validated quote",
  QUOTE_REVISION_CREATED: "Created quote revision",
  QUOTE_SIGNED: "Quote signed",
  QUOTE_PDF_GENERATED: "Generated quote PDF",
  QUOTE_VIEWED: "Quote viewed",
  QUOTE_DELETED: "Deleted quote",
  QUOTE_ARCHIVED: "Archived quote",
  QUOTE_UNARCHIVED: "Unarchived quote",
  QUOTE_PUBLIC_ACCESS_REVOKED: "Revoked quote public access",
  QUOTE_PUBLIC_ACCESS_RESTORED: "Restored quote public access",
  TASK_PENDING_APPROVAL: "Task pending approval",
  TASK_APPROVED: "Task approved",
  TASK_CANCELLED: "Task cancelled",
  ONBOARDING_TASK_COMPLETED: "Checked off onboarding task",
  ONBOARDING_TASK_REOPENED: "Reopened onboarding task",
  ITEM_CREATED: "Created item",
  ITEM_UPDATED: "Updated item",
};

/** Set of opportunity-specific event types for quick membership checks. */
export const OPPORTUNITY_EVENT_TYPES_SET: ReadonlySet<string> = new Set(
  OPPORTUNITY_EVENT_TYPE_VALUES,
);

/** Set of quote-specific event types for quick membership checks. */
export const QUOTE_EVENT_TYPES_SET: ReadonlySet<string> = new Set(
  OPPORTUNITY_QUOTE_EVENT_TYPE_VALUES,
);

// ============================================================================
// API RESPONSE SCHEMAS
// ============================================================================

export const ListActivityEventsResponseSchema = z.object({
  events: z.array(ActivityEventSchema),
});
export type ListActivityEventsResponse = z.infer<
  typeof ListActivityEventsResponseSchema
>;
